'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useWillStore } from '@/lib/store';
import { createClient } from '@/lib/supabase/client';
import { Button } from '@/app/components/ui/Button';
import { Input } from '@/app/components/ui/Input';
import { Label } from '@/app/components/ui/Label';
import { WillCallbackForm } from '@/app/components/form/WillCallbackForm';
import { ShieldCheck, ClipboardCheck, MapPin, Globe, Briefcase, PhoneCall, ArrowRight, Check, Mail, Loader2 } from 'lucide-react';
import Link from 'next/link';

export const EligibilityCheck = () => {
  const { isEligible, isCapable, setEligibility, setCapability, setSuitability, setData, setAuth, data } = useWillStore();

  // Local state for steps
  // 0: Location (England/Wales)
  // 1: Assets (UK)
  // 2: Business
  // 3: Start (Name/Email)
  // 4: Not Eligible (Callback)
  const [currentStep, setCurrentStep] = useState(0);

  // Local state for answers
  const [locationAnswer, setLocationAnswer] = useState<boolean | null>(null);
  const [assetsAnswer, setAssetsAnswer] = useState<boolean | null>(null);
  const [businessAnswer, setBusinessAnswer] = useState<boolean | null>(null);

  // Local state for start step
  // Removed upfront email/name collection


  const handleStartGuest = () => {
    // Set eligibility flags to true to allow access to the form
    setEligibility(true);
    setCapability(true);
    setSuitability(true);
  };


  const handleLocationAnswer = (answer: boolean) => {
    setLocationAnswer(answer);
    setCurrentStep(1);
  };

  const handleAssetsAnswer = (answer: boolean) => {
    setAssetsAnswer(answer);
    setCurrentStep(2);
  };

  const handleBusinessAnswer = (answer: boolean) => {
    setBusinessAnswer(answer);
    const isUserEligible = locationAnswer === true && assetsAnswer === true && !answer;

    if (isUserEligible) {
      setCurrentStep(3);
    } else {
      setCurrentStep(4);
    }
  };


  // Step 1: Verification (Location & Assets)
  if (isEligible === null || isCapable === null) {
    const renderStepLabel = () => {
      if (currentStep === 0) return 'Question 1 of 3';
      if (currentStep === 1) return 'Question 2 of 3';
      if (currentStep === 2) return 'Question 3 of 3';
      return null;
    };

    return (
      <div className="form-container pb-10" style={{ marginTop: '2.5rem' }}>
        <div className="animate-fade-in-up max-w-xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-foreground/10 p-8 sm:p-10 relative overflow-hidden">

            <div className="relative z-10">
              {currentStep === 0 && (
                <div className="animate-fade-in">
                  <div className="flex justify-center mb-6">
                    <div className="flex flex-col items-center gap-2">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
                        <MapPin className="w-8 h-8 text-primary" />
                      </div>
                      {renderStepLabel() && (
                        <p className="text-xs font-semibold uppercase tracking-wide text-teal-600">
                          {renderStepLabel()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-center space-y-4 mt-5">
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-primary">
                      Do you live in England or Wales?
                    </h2>
                  </div>
                  <div className="flex gap-4 max-w-md mx-auto pt-10">
                    <Button
                      size="lg"
                      onClick={() => handleLocationAnswer(true)}
                      className="flex-1 h-14 text-lg rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-primary/10"
                      variant={locationAnswer === true ? 'premium' : 'outline'}
                    >
                      Yes
                    </Button>
                    <Button
                      size="lg"
                      onClick={() => handleLocationAnswer(false)}
                      className="flex-1 h-14 text-lg rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                      variant={locationAnswer === false ? 'destructive' : 'outline'}
                    >
                      No
                    </Button>
                  </div>
                  <div className="mt-8 text-center max-w-md mx-auto px-4">
                    <p className="text-sm text-muted-foreground/80 leading-relaxed italic mb-4">
                      We need to confirm your location to ensure we apply the correct laws.
                    </p>
                    <div className="flex justify-center">
                      <Link href="/sign-in" className="inline-flex">
                        <Button variant="outline" size="sm" className="h-9 px-4 rounded-full text-sm border-gray-200 hover:bg-primary/5">
                          Already have a Will? <span className="font-semibold text-primary ml-2">Sign in</span>
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 1 && (
                <div className="animate-fade-in">
                  <div className="flex justify-center mb-6">
                    <div className="flex flex-col items-center gap-2">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
                        <Globe className="w-8 h-8 text-primary" />
                      </div>
                      {renderStepLabel() && (
                        <p className="text-xs font-semibold uppercase tracking-wide text-teal-600">
                          {renderStepLabel()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-center space-y-4 mt-5">
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-primary">
                      Is everything you own in the UK?
                    </h2>
                  </div>
                  <div className="flex gap-4 max-w-md mx-auto pt-10">
                    <Button
                      size="lg"
                      onClick={() => handleAssetsAnswer(true)}
                      className="flex-1 h-14 text-lg rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-primary/10"
                      variant={assetsAnswer === true ? 'premium' : 'outline'}
                    >
                      Yes
                    </Button>
                    <Button
                      size="lg"
                      onClick={() => handleAssetsAnswer(false)}
                      className="flex-1 h-14 text-lg rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                      variant={assetsAnswer === false ? 'destructive' : 'outline'}
                    >
                      No
                    </Button>
                  </div>
                  <div className="mt-8 text-center max-w-md mx-auto px-4">
                    <p className="text-sm text-muted-foreground/80 leading-relaxed italic mb-4">
                      Foreign assets can complicate your estate.
                    </p>
                    <div className="flex justify-center">
                      <Link href="/sign-in" className="inline-flex">
                        <Button variant="outline" size="sm" className="h-9 px-4 rounded-full text-sm border-gray-200 hover:bg-primary/5">
                          Already have a Will? <span className="font-semibold text-primary ml-2">Sign in</span>
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="animate-fade-in">
                  <div className="flex justify-center mb-6">
                    <div className="flex flex-col items-center gap-2">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
                        <Briefcase className="w-8 h-8 text-primary" />
                      </div>
                      {renderStepLabel() && (
                        <p className="text-xs font-semibold uppercase tracking-wide text-teal-600">
                          {renderStepLabel()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-center space-y-4 mt-5">
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-primary">
                      Do you own a business?
                    </h2>
                  </div>
                  <div className="flex gap-4 max-w-md mx-auto pt-10">
                    <Button
                      size="lg"
                      onClick={() => handleBusinessAnswer(true)}
                      className="flex-1 h-14 text-lg rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                      variant={businessAnswer === true ? 'destructive' : 'outline'}
                    >
                      Yes
                    </Button>
                    <Button
                      size="lg"
                      onClick={() => handleBusinessAnswer(false)}
                      className="flex-1 h-14 text-lg rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-primary/10"
                      variant={businessAnswer === false ? 'premium' : 'outline'}
                    >
                      No
                    </Button>
                  </div>
                  <div className="mt-8 text-center max-w-md mx-auto px-4">
                    <p className="text-sm text-muted-foreground/80 leading-relaxed italic mb-4">
                      Business assets may need special tax planning.
                    </p>
                    <div className="flex justify-center">
                      <Link href="/sign-in" className="inline-flex">
                        <Button variant="outline" size="sm" className="h-9 px-4 rounded-full text-sm border-gray-200 hover:bg-primary/5">
                          Already have a Will? <span className="font-semibold text-primary ml-2">Sign in</span>
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="animate-fade-in space-y-8">
                  <div className="flex flex-col items-center text-center gap-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                      <ClipboardCheck className="w-8 h-8 text-primary animate-in zoom-in duration-500" />
                    </div>
                    <div className="space-y-2">
                      <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-primary">
                        You&apos;re ready to begin!
                      </h2>
                      <p className="text-muted-foreground">
                        Let&apos;s get your Will sorted in just a few minutes.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <Button
                      size="lg"
                      onClick={handleStartGuest}
                      className="w-full h-14 text-lg flex items-center justify-center gap-2"
                      variant="primary"
                    >
                      Start my Online Will
                      <ArrowRight className="w-5 h-5" />
                    </Button>

                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">
                        Already have an account?{' '}
                        <Link href="/sign-in" className="text-primary font-medium hover:underline">
                          Sign in
                        </Link>
                      </p>
                    </div>

                    <div className="flex flex-col items-center gap-2 pt-6 border-t border-foreground/5">
                      <div className="flex items-center gap-2 text-teal/80">
                        <ShieldCheck className="w-4 h-4" />
                        <span className="font-bold text-xs uppercase tracking-wider">Your Data is Secure</span>
                      </div>
                      <p className="text-xs text-muted-foreground text-center w-full leading-relaxed max-w-xs mx-auto">
                        We will never share your information with third parties and only use it to contact you about your Will.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="animate-fade-in">
                  <div className="flex flex-col items-center text-center gap-6 mb-10">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                      <PhoneCall className="w-8 h-8 text-primary animate-in zoom-in duration-500" />
                    </div>
                  </div>

                  <WillCallbackForm
                    source="eligibility-check"
                    livesInEnglandWales={locationAnswer}
                    assetsInUk={assetsAnswer}
                    ownsBusiness={businessAnswer}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
